/**
 * The mark on the left of every row — list engine v2 principle 1, "read by
 * shape first".
 *
 * A screen full of rows is a screen full of text, and text all looks the same
 * at a glance. The mark is what lets somebody find the row they were on after
 * looking away: a face on Contacts, a map square on Properties, a machine on
 * Equipment. It sits in a pinned column, so it is still there when the table
 * has scrolled sideways.
 *
 * Every mark is 28px in a 44px column, uses the palette from §3.1 and nothing
 * else, and falls back to something rather than to nothing — a contact with no
 * photo gets initials, a property with no zone gets the plain line colour. A
 * mark that sometimes is not there is a column of holes.
 */
import type { ReactNode } from 'react'
import { Avatar } from './primitives.js'
import { Glyph, markInk, markTint, type GlyphSet } from '../frame/mark-glyphs.js'
import { VISIT_STATUS_AS_JOB } from './record-list.js'
import type { ListRow } from '../lib/use-list.js'

const SIZE = 28

/*
 * `Square` is gone. It was the 28px tinted square with a glyph in it, and the
 * three marks that used it — equipment, invoice, visit — now draw a glyph tile
 * that scales, a ring and a calendar card. `MarkTile` below is the base the
 * scaling ones share; a fixed-size square had no callers left.
 */

/** A face, or the initials the product already draws everywhere else. */
export function contactMark(row: ListRow): ReactNode {
  const name = String(row.name ?? '').trim()
  const photo = row.photoFileId ? `/api/v1/files/${String(row.photoFileId)}` : undefined
  return <Avatar name={name || '—'} size={SIZE} src={photo} />
}

/**
 * A plan view of the site — the mark the property mockup draws.
 *
 * `profiles-main.png` does not put a photograph or a glyph beside a property:
 * it draws a small map. This is that, as an SVG, at any size.
 *
 * **It is drawn, not fetched.** Two properties on the same street do not get
 * the same picture and neither of them gets the real one: the roads, the park
 * and the buildings come from a hash of the property's own id, so a row is
 * recognisable and consistent without being true. A Google Static Maps tile is
 * billed per request and a list draws fifty a page, so the version that costs
 * money is a spending decision for whoever pays the bill — see
 * `docs/DEPLOYING.md`. What this gives is what the mark is *for*: two
 * properties in the same zone read as the same colour, and no two rows look
 * identical.
 *
 * Zoomed out from the first version, which was one road and one building and
 * read as an abstract logo. A crossroads, a block of green, and four buildings
 * of different sizes is unmistakably a map at 28px — which is the whole job,
 * because the mark is there to be recognised rather than read.
 */
export function PropertyMap({
  seed, colour, size = SIZE, radius = 8, title, pin = true,
}: {
  /** Anything stable about the property — its id. Two rows never match. */
  seed: string
  /** The zone's colour, or null for the default green. */
  colour: string | null
  size?: number
  radius?: number
  title?: string
  /**
   * Draw the pin. Off where the caller draws its own.
   *
   * The pin is sized in the viewBox, so a mark blown up to fill a 220px card
   * gets a 105px pin — a marker the size of the town it is marking. A panel
   * that wants the streets as a backdrop turns this off and puts a real icon
   * on top at a real size.
   */
  pin?: boolean
}) {
  const hash = hashOf(seed)
  // The zone's own colour is data; the fallback and the roads come from the
  // palette, never from a hex typed here (the design-token guard checks).
  const base = colour ?? 'rgb(var(--color-accent))'
  const paper = 'rgb(var(--color-surface))'
  /*
   * The radius is in the id, not just the seed. The same property is drawn at
   * two sizes on two pages, and two clip paths sharing an id would leave the
   * larger one clipped to the smaller one's corners.
   */
  const clip = `site-${hash.toString(36)}-${Math.round(radius)}`

  /*
   * Two kinds of town, chosen by the hash: a crossroads, or one road running
   * across at an angle. The mockup has both, and two shapes is enough variety
   * at 28px — a third is a difference nobody can see.
   */
  const diagonal = hash % 5 === 0
  const roadY = 10 + (hash >> 3) % 9
  const roadX = 9 + (hash >> 7) % 11
  const angle = diagonal ? -38 + (hash % 20) : 0
  // One darker block, in one of the four quadrants, on about half of them.
  const block = (hash >> 11) % 2 === 0 ? (hash >> 13) % 4 : null

  return (
    <svg
      viewBox="0 0 44 44"
      width={size}
      height={size}
      role="img"
      aria-label={title ?? 'Site'}
      className="shrink-0"
    >
      {title && <title>{title}</title>}

      {/*
        * Everything is drawn inside this. An SVG does not clip a sibling to a
        * rounded rect, so the roads and the green block used to run out past
        * the corners — which is what made a tile with a 12px radius read as
        * "a circle with a corner sticking out of it".
        */}
      <defs>
        <clipPath id={clip}>
          <rect x="0" y="0" width="44" height="44" rx={(radius / SIZE) * 44} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clip})`}>
        <rect x="0" y="0" width="44" height="44" fill={base} fillOpacity="0.16" />

        {block !== null && (
          <rect
            x={block % 2 === 0 ? 4 : roadX + 6}
            y={block < 2 ? 4 : roadY + 6}
            width={9 + (hash >> 17) % 6}
            height={7 + (hash >> 19) % 5}
            rx="1.5"
            fill={base}
            fillOpacity="0.26"
          />
        )}

        <g transform={`rotate(${angle} 22 22)`}>
          <line x1="-14" y1={roadY} x2="58" y2={roadY}
            stroke={paper} strokeWidth="4" strokeLinecap="butt" />
          {!diagonal && (
            <line x1={roadX} y1="-14" x2={roadX} y2="58"
              stroke={paper} strokeWidth="3.4" strokeLinecap="butt" />
          )}
        </g>
      </g>

      {/*
        * The pin, centred, solid, with the hole punched through it. It was
        * translated into the bottom-right corner, which read as a stray mark
        * rather than as "this is a place".
        */}
      {pin && (
      <g transform="translate(22 21.5)">
        <path
          d="M0 10.5 C0 10.5 7.6 3.2 7.6 -2.4 A7.6 7.6 0 1 0 -7.6 -2.4 C-7.6 3.2 0 10.5 0 10.5 Z"
          fill={base}
        />
        <circle cx="0" cy="-2.6" r="2.7" fill={paper} />
      </g>
      )}
    </svg>
  )
}

/** A small stable number from a string, so a property always draws the same. */
function hashOf(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return Math.abs(hash)
}

export function propertyMark(row: ListRow): ReactNode {
  const colour = typeof row.zoneColour === 'string' && /^#[0-9a-f]{6}$/i.test(row.zoneColour)
    ? row.zoneColour
    : null
  const zone = String(row.zoneName ?? 'No zone')
  return <PropertyMap seed={String(row.id ?? row.name ?? '')} colour={colour} title={zone} />
}

/* ========================================================================
 * The four marks — `docs/list-marks.md` §1–§3 and `marks-and-icons.md` §4
 *
 * Aaron: "I'm not happy with the jobs, tickets, invoice or equipment lists…
 * The iconography and different versions claude mocked up for invoices and
 * the visits is amazing."
 *
 * Each is `(row, size) => ReactNode` at 28 (a list row), 44 (a phone card) and
 * 56 (a record header), so one function serves all three and a row can never
 * disagree with the record it opens. Radius scales at 0.29 × size and the
 * glyph at 0.57 × size, so the big one is the small one enlarged rather than a
 * different drawing.
 * ===================================================================== */

/** The sizes a mark is drawn at. Anything else is a caller's mistake. */
export type MarkSize = 28 | 44 | 56

const radiusFor = (size: number) => Math.round(size * 0.29)

/** The status dot: the same colour as the chip on the row, by construction. */
function StatusDot({ size, css }: { size: number; css: string }) {
  /* 8px at 28, 14 at 44, 18 at 56 (§1), with a paper ring so it reads against
     the tile whatever the tint behind it. */
  const dot = size <= 28 ? 8 : size <= 44 ? 14 : 18
  const ring = size <= 28 ? 1.5 : size <= 44 ? 2 : 2.5
  return (
    <span
      aria-hidden="true"
      className="absolute rounded-full"
      style={{
        width: dot, height: dot, right: -ring, bottom: -ring,
        background: css,
        boxShadow: `0 0 0 ${ring}px rgb(var(--color-surface))`,
      }}
    />
  )
}

/**
 * The shared tile every glyph mark is built on — a tint, a glyph, and room for
 * a dot and a badge.
 *
 * `Square` above stays for what still uses it; this is the one that scales,
 * and the one the spec calls `Tile`.
 */
export function MarkTile({
  set, slug, colour, size, title, dot, badge,
}: {
  set: GlyphSet
  slug: string | null | undefined
  colour: string | null | undefined
  size: number
  title: string
  /** A CSS colour for the status dot, or nothing for no dot. */
  dot?: string | null
  badge?: ReactNode
}) {
  return (
    <span
      title={title}
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{
        width: size, height: size, borderRadius: radiusFor(size),
        background: markTint(colour), color: markInk(colour),
      }}
    >
      <Glyph
        set={set}
        slug={slug}
        size={Math.round(size * 0.57)}
        strokeWidth={size >= 44 ? 1.8 : 1.9}
      />
      {badge}
      {dot && <StatusDot size={size} css={dot} />}
    </span>
  )
}

/**
 * Which statuses have a chip pair behind them, per group.
 *
 * A mark asks for `--status-<group>-<status>-fg` the way `StatusChip` does,
 * and an undefined variable is not an error: `var()` resolves to nothing,
 * `rgb()` is invalid and the dot draws with no colour. So a mark checks
 * first, and these are the lists it checks against — **exactly the keys of
 * `semantic.<group>` in `design/tokens.json`**, which
 * `tests/design-tokens.test.ts` asserts, because a list kept by hand beside a
 * palette kept in a JSON file is two answers to one question. The three
 * statuses that were missing from the palette (equipment `retired`, invoice
 * `voided`, PO `cancelled`) were missing from here too.
 */
export const MARK_STATUSES: Record<string, readonly string[]> = {
  job: [
    'requested', 'reviewed', 'quoted', 'approved', 'scheduled', 'in_progress',
    'completed', 'returned', 'invoiced', 'paid', 'declined', 'cancelled', 'written_off',
  ],
  equipment: [
    'available', 'assigned', 'due_for_service', 'in_shop', 'out_of_service', 'retired',
  ],
  invoice: [
    'draft', 'awaiting_approval', 'scheduled', 'sent', 'viewed', 'partially_paid',
    'paid', 'overdue', 'disputed', 'bounced', 'voided',
  ],
}

/** Only a status the tokens actually define; anything else draws no dot. */
const tokenOr = (group: string, status: string) =>
  MARK_STATUSES[group]?.includes(status) ? `rgb(var(--status-${group}-${status}-fg))` : null

/**
 * A job: the division's tile, with a dot for where the job stands.
 *
 * This replaces the 4px colour line (`stripe`), which was the one mark in the
 * app that said nothing about *what* the row is — only, faintly, about its
 * status, in a place the eye does not go.
 *
 * `divisionIcon` and `divisionColour` ride along on every row (`JOBS_AREA`,
 * `system: true`), joined from `divisions` on the code `jobs.service_line`
 * holds. A job whose division was renamed gets null and falls back to a wrench
 * on slate — a mark that is there rather than a hole in the column.
 */
export function jobMark(row: ListRow, size: MarkSize = 28): ReactNode {
  const status = String(row.status ?? '')
  const division = String(row.serviceLine ?? 'No division')
  const label = STATUS_WORDS[status] ?? status.replace(/_/g, ' ')
  return (
    <MarkTile
      set="division"
      slug={typeof row.divisionIcon === 'string' ? row.divisionIcon : null}
      colour={typeof row.divisionColour === 'string' ? row.divisionColour : null}
      size={size}
      /* §4: the title names what the mark encodes, both halves of it. */
      title={`${division} · ${label}`}
      dot={tokenOr('job', status)}
      badge={row.jobType === 'recurring'
        ? <RecurringBadge size={size} ink={markInk(
            typeof row.divisionColour === 'string' ? row.divisionColour : null)} />
        : undefined}
    />
  )
}

/** Sentence case for a status, since `in_progress` is not a word. */
const STATUS_WORDS: Record<string, string> = {
  in_progress: 'In progress',
  due_for_service: 'Due for service',
  in_shop: 'In shop',
  out_of_service: 'Out of service',
  awaiting_approval: 'Awaiting approval',
  partially_paid: 'Partially paid',
  written_off: 'Written off',
}

/**
 * The loop on a recurring job (§1) — top-left, paper behind the division's
 * ink, because a weekly mow and a one-off install are the two rows hardest to
 * tell apart on a board and the difference changes what you do next.
 */
function RecurringBadge({ size, ink }: { size: number; ink: string }) {
  const badge = size <= 28 ? 11 : size <= 44 ? 16 : 20
  return (
    <span
      aria-hidden="true"
      className="absolute inline-flex items-center justify-center rounded-full"
      style={{
        width: badge, height: badge, left: -2, top: -2,
        background: 'rgb(var(--color-surface))',
        boxShadow: '0 0 0 1px rgb(var(--color-line-soft))',
        color: ink,
      }}
    >
      <svg
        viewBox="0 0 24 24" width={Math.round(badge * 0.7)} height={Math.round(badge * 0.7)}
        fill="none" stroke="currentColor" strokeWidth="2.6"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M17 2l4 4-4 4" />
        <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
        <path d="M7 22l-4-4 4-4" />
        <path d="M21 13v1a4 4 0 0 1-4 4H3" />
      </svg>
    </span>
  )
}

/**
 * Equipment: what kind of machine it is, tinted by its own kind, with a dot
 * for what the shop thinks of it.
 *
 * The glyph is picked on the **type**, never on the piece — Aaron: "icons
 * should be selected on categories of equipment not each piece" — so a company
 * with forty mowers draws one mower and forty rows of it, which is the point:
 * the column tells trucks from mowers at a glance and the dot tells which one
 * is in the shop.
 *
 * It was a grey machine on every row, tinted only by status, so the whole
 * column was one shape and the fleet was unreadable by kind.
 */
export function equipmentMark(row: ListRow, size: MarkSize = 28): ReactNode {
  const status = String(row.status ?? 'available')
  const kind = String(row.typeName ?? 'Equipment')
  const label = STATUS_WORDS[status] ?? status.replace(/_/g, ' ')
  return (
    <MarkTile
      set="equipment"
      slug={typeof row.typeIcon === 'string' ? row.typeIcon : null}
      colour={typeof row.typeColour === 'string' ? row.typeColour : null}
      size={size}
      title={`${kind} · ${label}`}
      dot={tokenOr('equipment', status)}
    />
  )
}

/**
 * A visit: a calendar card, because a visit has no name.
 *
 * It is a crew, a property and a **date**, and the one pair of rows a Visits
 * list is hardest to tell apart by reading is two visits to the same property
 * on different days. So the mark is the date, drawn as a card: a band carrying
 * the month in three letters, and the day beneath it.
 *
 * The old version put only the day number in a tinted square, which could not
 * tell September the 3rd from October the 3rd — the two rows a crew board puts
 * side by side in a rolling season.
 *
 * Tokens: the visit statuses map onto the job ones (`VISIT_STATUS_AS_JOB`),
 * which is what `list-marks.md` §2 means by "aliases of the job tokens" —
 * a mapping rather than a second set of variables, so the mark and the chip on
 * the row cannot drift apart.
 */
export function visitMark(row: ListRow, size: MarkSize = 28): ReactNode {
  const raw = String(row.status ?? '')
  const status = VISIT_STATUS_AS_JOB[raw] ?? 'scheduled'
  const fg = `rgb(var(--status-job-${status}-fg))`
  const bg = `rgb(var(--status-job-${status}-bg))`

  /* A plain `YYYY-MM-DD`, read as text. Parsing it as a Date and asking for
     its day gives yesterday's number west of Greenwich. */
  const date = String(row.scheduledDate ?? '')
  const month = date.slice(5, 7)
  const day = date.slice(8, 10).replace(/^0/, '')
  const band = date ? MONTHS[month] ?? '—' : 'TBD'

  const bandHeight = Math.round(size * 0.32)
  const monthSize = size <= 28 ? 6 : size <= 44 ? 9 : 12
  const daySize = size <= 28 ? 12 : size <= 44 ? 19 : 24

  return (
    <span
      title={date || 'Not scheduled'}
      className="relative inline-flex shrink-0 flex-col items-stretch overflow-hidden"
      style={{ width: size, height: size, borderRadius: radiusFor(size), background: bg }}
    >
      <span
        aria-hidden="true"
        className="flex items-center justify-center font-semibold uppercase leading-none
                   tracking-[0.04em] text-white"
        style={{ height: bandHeight, background: fg, fontSize: monthSize }}
      >
        {band}
      </span>
      <span
        aria-hidden="true"
        className="flex flex-1 items-center justify-center font-bold leading-none tabular-nums"
        style={{ color: fg, fontSize: daySize }}
      >
        {day || '–'}
      </span>
      {/* Skipped: a strike across the day, in the status ink (§2). A rained-off
          visit is still on the board and still has to be read as not done. */}
      {raw === 'skipped' && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              `linear-gradient(114deg, transparent calc(50% - 1px), ${fg} calc(50% - 1px),`
              + ` ${fg} calc(50% + 1px), transparent calc(50% + 1px))`,
          }}
        />
      )}
    </span>
  )
}

const MONTHS: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun',
  '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
}

/**
 * The visit page's calendar card (`docs/visit-page.md` §7.2, §18; folder 50,
 * Boards 1, 13, 14) — `visitMark`'s card with the two things a *visit* (a work
 * item, not a day) carries: **a second card behind it** for a visit of more
 * than one day (3 px right and up; 2 at 28) and **the loop badge** top-left on
 * every occurrence of a series.
 *
 * **Opt-in, and a separate function on purpose**: `visitMark` above draws the
 * Visits list (a day per row, the job tokens) and must not move
 * (do-not-substitute 0); this one is called only by the visit page's own
 * `VisitMark` (`client/src/visit/visit-mark.tsx`), which passes the visit
 * statuses' tokens. The board's band is 700 with 0.06em tracking, and a
 * skipped day is struck by a 2 px rule across the number.
 */
export function CalendarCard({
  date, fg, bg, size, multiDay = false, series = null, skipped = false, title,
}: {
  /** `YYYY-MM-DD`, or null for the band *TBD* and a dash. */
  date: string | null
  /** The band and the day number. */
  fg: string
  /** The card. */
  bg: string
  size: MarkSize
  multiDay?: boolean
  /** The loop badge's ink, or null for no badge. */
  series?: string | null
  skipped?: boolean
  title?: string
}): ReactNode {
  const month = date ? date.slice(5, 7) : ''
  const day = date ? date.slice(8, 10).replace(/^0/, '') : ''
  const band = date ? (MONTHS[month] ?? '—').toUpperCase() : 'TBD'
  const offset = size <= 28 ? 2 : 3
  const radius = radiusFor(size)
  const bandHeight = Math.round(size * 0.32)
  const monthSize = size <= 28 ? 6 : size <= 44 ? 9 : 12
  const daySize = size <= 28 ? 12 : size <= 44 ? 19 : 24
  const badge = size <= 28 ? 14 : size <= 44 ? 18 : 20
  const glyph = size <= 28 ? 8 : size <= 44 ? 10 : 11

  const card = (
    <span
      title={title ?? (date || undefined)}
      className="relative inline-flex shrink-0"
      style={{ width: size + (multiDay ? offset : 0), height: size, paddingTop: multiDay ? offset : 0, boxSizing: 'content-box' }}
      data-mark="visit"
      data-multi-day={multiDay ? '' : undefined}
    >
      {multiDay && (
        <span
          aria-hidden="true"
          className="absolute top-0 box-border border border-surface opacity-90"
          style={{ left: offset, width: size, height: size, borderRadius: radius, background: bg }}
        />
      )}
      <span
        className="relative z-[1] box-border inline-flex flex-col overflow-hidden"
        style={{ width: size, height: size, borderRadius: radius, background: bg }}
      >
        <span
          aria-hidden="true"
          className="flex items-center justify-center font-bold tracking-[.06em] text-white"
          style={{ height: bandHeight, background: fg, fontSize: monthSize }}
        >
          {band}
        </span>
        <span
          aria-hidden="true"
          className="relative flex flex-1 items-center justify-center font-bold tracking-[-.02em]"
          style={{ color: fg, fontSize: daySize }}
        >
          {day || '—'}
          {skipped && (
            <i
              aria-hidden="true"
              className="absolute left-[18%] right-[18%] top-1/2 block h-0.5 -rotate-[24deg]"
              style={{ background: fg }}
            />
          )}
        </span>
      </span>
    </span>
  )
  if (!series) return card
  return (
    <span className="relative inline-flex shrink-0" data-series="">
      {card}
      <span
        aria-hidden="true"
        className="absolute z-[3] box-border inline-flex items-center justify-center rounded-full border-[1.5px] border-line bg-surface"
        style={{ width: badge, height: badge, left: -badge / 2, top: -badge / 2, color: series }}
      >
        <svg viewBox="0 0 24 24" width={glyph} height={glyph} fill="none" stroke="currentColor" strokeWidth={2.4}
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12a8 8 0 1 0 3-6.2L4 8M4 3v5h5" />
        </svg>
      </span>
    </span>
  )
}

/**
 * An invoice: a ring that fills as it is paid.
 *
 * The one number somebody scanning an invoice list wants is how much of it has
 * come in, and it was not on the row at all — the mark was a receipt glyph,
 * the same shape on every row, saying only what the Status column said louder.
 * A ring says "half paid" in the width of a glance, and the Balance column
 * agrees with it by construction because both read `amount` and `paid`.
 *
 * **No arc at 0%** — a round cap would draw a dot, which reads as a little
 * paid rather than none. Draft is a dashed ring, because a draft has no
 * progress to show and an empty solid ring looks like a failure.
 */
export function invoiceMark(row: ListRow, size: MarkSize = 28): ReactNode {
  const status = String(row.status ?? 'draft')
  const known = MARK_STATUSES.invoice!.includes(status)
  const fg = known ? `rgb(var(--status-invoice-${status}-fg))` : 'rgb(var(--color-text-faint))'
  const bg = known ? `rgb(var(--status-invoice-${status}-bg))` : 'rgb(var(--color-field))'

  /*
   * The arc is what has been **settled**, which is the invoice's total less
   * what is still owed — not `paid` alone. `balance` already nets off
   * allocations, credits and write-offs (§F.2), so a written-off invoice draws
   * a full ring rather than an empty one that somebody would chase. Both
   * columns are area defaults and both are on the record payload, so the ring,
   * the Balance column and the record header cannot disagree.
   */
  const amount = Number(row.amount ?? 0)
  const owed = row.balance !== undefined && row.balance !== null
    ? Number(row.balance)
    : amount - Number(row.paid ?? 0)
  const share = amount > 0 ? Math.max(0, Math.min(1, (amount - owed) / amount)) : 0

  const stroke = size <= 28 ? 2.5 : size <= 44 ? 4 : 4
  const r = (size - stroke) / 2
  const circumference = 2 * Math.PI * r
  const draft = status === 'draft'
  const settled = status === 'paid' || (amount > 0 && owed <= 0)

  return (
    <span
      title={`${STATUS_WORDS[status] ?? status.replace(/_/g, ' ')}`
        + (amount > 0 ? ` · ${Math.round(share * 100)}% paid` : '')}
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={draft ? 'rgb(var(--color-line-strong))' : bg}
          strokeWidth={stroke}
          strokeDasharray={draft ? `${stroke * 1.6} ${stroke * 1.6}` : undefined}
        />
        {!draft && share > 0 && (
          /* From twelve o'clock, clockwise. The rotation is on the circle so
             the dash offset stays a plain length rather than trigonometry. */
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none" stroke={fg} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${circumference * share} ${circumference}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <span
        aria-hidden="true"
        className="absolute inset-0 flex items-center justify-center font-bold leading-none"
        style={{ color: draft ? 'rgb(var(--color-text-faint))' : fg,
          fontSize: Math.round(size * 0.46) }}
      >
        {settled
          ? (
            <svg
              viewBox="0 0 24 24" width={Math.round(size * 0.44)}
              height={Math.round(size * 0.44)} fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          )
          : '$'}
      </span>
    </span>
  )
}
